/** Path segment identifier for admin-v2 routes embedded in the shell app. */
const ADMIN_V2_SEGMENT = '/admin-v2';

/**
 * Checks if a location contains the embedded admin-v2 segment.
 * @param location - The location path to check.
 * @returns True if the location includes the admin-v2 segment.
 */
function isEmbeddedAdminV2(location: string) {
    return location.includes(ADMIN_V2_SEGMENT);
}

/**
 * Extracts the base path from a location, removing the admin-v2 segment if present.
 * @param originalLocation - The original location path.
 * @returns The base path (everything before the admin-v2 segment, or the original location).
 */
function getBasePath(originalLocation: string) {
    if (isEmbeddedAdminV2(originalLocation)) {
        return originalLocation.split(ADMIN_V2_SEGMENT)[0];
    }
    return originalLocation;
}

/**
 * Extracts the inner path from a location (everything after the admin-v2 segment).
 * @param originalLocation - The original location path.
 * @returns The inner path after the admin-v2 segment, or null if not present.
 */
function getInnerPath(originalLocation: string) {
    if (isEmbeddedAdminV2(originalLocation)) {
        return originalLocation.split(ADMIN_V2_SEGMENT)[1];
    }
    return null;
}

/**
 * Constructs the admin app path by prepending the admin route prefix.
 * @param innerPath - The inner path to append to the admin route.
 * @returns The constructed admin app path.
 */
function getAdminAppPath(innerPath: string): string {
    return `/../admin${innerPath}`;
}

/**
 * Maps an embedded admin-v2 location to the corresponding admin app location.
 * Combines the base path with the admin app path constructed from the inner path.
 * @param originalLocation - The original location path.
 * @returns The mapped admin app location.
 */
function getAdminAppLocation(originalLocation: string) {
    let mappedLocation = getBasePath(originalLocation);
    const innerPath = getInnerPath(originalLocation);
    if (innerPath !== null) {
        const newPath = getAdminAppPath(innerPath);
        if (newPath) {
            mappedLocation = `${mappedLocation}${newPath}`;
        }
    }
    return mappedLocation;
}

/**
 * Maps a location path, converting embedded admin-v2 routes to admin app routes.
 * If the location contains the admin-v2 segment, it is transformed to the corresponding admin app path.
 * Otherwise, the original location is returned unchanged.
 * @param originalLocation - The original location path to map.
 * @returns The mapped location path.
 */
export function getMappedLocation(originalLocation: string): string {
    let mappedLocation = originalLocation;

    if (isEmbeddedAdminV2(originalLocation)) {
        mappedLocation = getAdminAppLocation(originalLocation);
    }

    return mappedLocation;
}
